/**
 * Samedi Theme Utilities
 * Shared functions used across all sections
 * Fixes: localStorage crashes, fetch timeouts, race conditions
 */

(function(window) {
  'use strict';

  // ============================================================================
  // SAFE LOCALSTORAGE WRAPPER (Critical Fix #2)
  // Prevents crashes in Safari private browsing mode
  // ============================================================================

  window.SamediUtils = window.SamediUtils || {};

  /**
   * Safe localStorage wrapper
   * @param {string} action - 'get' or 'set'
   * @param {string} key - localStorage key
   * @param {*} value - value to set (for 'set' action)
   * @returns {*} value for 'get', boolean for 'set'
   */
  window.SamediUtils.safeLocalStorage = function(action, key, value) {
    try {
      if (action === 'get') {
        return localStorage.getItem(key);
      }
      if (action === 'set') {
        localStorage.setItem(key, value);
        return true;
      }
    } catch (e) {
      // Fallback to session storage
      try {
        if (action === 'get') {
          return sessionStorage.getItem(key);
        }
        if (action === 'set') {
          sessionStorage.setItem(key, value);
          return true;
        }
      } catch (sessionError) {
        // If both fail, use in-memory fallback
        window.__samediMemoryStorage = window.__samediMemoryStorage || {};
        if (action === 'get') {
          return window.__samediMemoryStorage[key] || null;
        }
        if (action === 'set') {
          window.__samediMemoryStorage[key] = value;
          return true;
        }
      }
    }
    return action === 'get' ? null : false;
  };

  // ============================================================================
  // FETCH WITH TIMEOUT (Critical Fix #5)
  // Prevents hanging requests
  // ============================================================================

  /**
   * Fetch wrapper with timeout
   * @param {string} url - URL to fetch
   * @param {object} options - fetch options
   * @param {number} timeout - timeout in milliseconds (default: 10000)
   * @returns {Promise<Response>}
   */
  window.SamediUtils.fetchWithTimeout = async function(url, options = {}, timeout = 10000) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - please check your connection');
      }
      throw error;
    }
  };

  // ============================================================================
  // EVENT-BASED AUDIENCE SWITCHER (High Priority Fix #4)
  // Prevents race conditions when multiple sections listen for changes
  // ============================================================================

  /**
   * Get current audience preference
   * @returns {string} 'women' or 'men'
   */
  window.SamediUtils.getCurrentAudience = function() {
    return window.SamediUtils.safeLocalStorage('get', 'audience') || 'women';
  };

  /**
   * Set audience and broadcast change event
   * @param {string} audience - 'women' or 'men'
   */
  window.SamediUtils.setAudience = function(audience) {
    if (audience !== 'women' && audience !== 'men') {
      return false;
    }

    const previousAudience = window.SamediUtils.getCurrentAudience();

    // Save to storage
    window.SamediUtils.safeLocalStorage('set', 'audience', audience);

    // Update body classes and attributes
    document.body.classList.remove('audience-women', 'audience-men');
    document.body.classList.add('audience-' + audience);
    document.body.setAttribute('data-audience', audience);

    // Broadcast event to all sections
    document.dispatchEvent(new CustomEvent('samediAudienceChange', {
      detail: {
        audience: audience,
        previousAudience: previousAudience
      }
    }));

    return true;
  };

  /**
   * Listen for audience changes
   * @param {Function} callback - function to call when audience changes
   * @returns {Function} cleanup function to remove listener
   */
  window.SamediUtils.onAudienceChange = function(callback) {
    const handler = function(event) {
      callback(event.detail.audience, event.detail.previousAudience);
    };

    document.addEventListener('samediAudienceChange', handler);

    // Return cleanup function
    return function() {
      document.removeEventListener('samediAudienceChange', handler);
    };
  };

  // ============================================================================
  // CART UTILITIES
  // ============================================================================

  /**
   * Add item to cart with retry logic
   * @param {object} data - cart data { id, quantity }
   * @param {number} maxRetries - maximum retry attempts
   * @returns {Promise<object>} cart response
   */
  window.SamediUtils.addToCartWithRetry = async function(data, maxRetries = 3) {
    let lastError = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const response = await window.SamediUtils.fetchWithTimeout('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data)
        }, 10000);

        if (!response.ok) {
          const error = await response.json().catch(() => ({ message: 'Unknown error' }));
          error.status = response.status;

          // Don't retry for client errors (422, 404)
          if (response.status === 422 || response.status === 404) {
            throw error;
          }

          lastError = error;

          // Wait before retrying (exponential backoff)
          if (attempt < maxRetries - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
            continue;
          }

          throw error;
        }

        return await response.json();

      } catch (error) {
        lastError = error;

        // Don't retry for network offline or client errors
        if (!navigator.onLine || error.status === 422 || error.status === 404) {
          throw error;
        }

        // Last attempt failed
        if (attempt === maxRetries - 1) {
          throw error;
        }

        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
      }
    }

    throw lastError;
  };

  /**
   * Update cart count in header
   * @returns {Promise<number>} cart item count
   */
  window.SamediUtils.updateCartCount = async function() {
    try {
      const response = await window.SamediUtils.fetchWithTimeout('/cart.js', {}, 5000);
      const cart = await response.json();

      // Update all cart count elements
      document.querySelectorAll('.cart-count, [data-cart-count]').forEach(element => {
        element.textContent = cart.item_count;
        element.setAttribute('data-cart-count', cart.item_count);
      });

      return cart.item_count;
    } catch (error) {
      return null;
    }
  };

  // ============================================================================
  // DOM UTILITIES
  // ============================================================================

  /**
   * Efficient DOM batch update using DocumentFragment
   * @param {HTMLElement} container - container element
   * @param {HTMLElement[]} elements - elements to append
   */
  window.SamediUtils.batchDOMUpdate = function(container, elements) {
    const fragment = document.createDocumentFragment();
    elements.forEach(el => fragment.appendChild(el));
    container.appendChild(fragment);
  };

  /**
   * Debounce function
   * @param {Function} func - function to debounce
   * @param {number} wait - milliseconds to wait
   * @returns {Function} debounced function
   */
  window.SamediUtils.debounce = function(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  /**
   * Throttle function
   * @param {Function} func - function to throttle
   * @param {number} limit - milliseconds to limit
   * @returns {Function} throttled function
   */
  window.SamediUtils.throttle = function(func, limit) {
    let inThrottle;
    return function(...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  };

  // ============================================================================
  // ANIMATION UTILITIES
  // ============================================================================

  /**
   * Add will-change property for animation, remove after
   * @param {HTMLElement} element - element to animate
   * @param {string} properties - CSS properties to animate
   * @param {number} duration - animation duration in ms
   */
  window.SamediUtils.optimizeAnimation = function(element, properties, duration) {
    element.style.willChange = properties;
    setTimeout(() => {
      element.style.willChange = 'auto';
    }, duration + 100);
  };

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  // Initialize body class for current audience on page load
  (function() {
    const currentAudience = window.SamediUtils.getCurrentAudience();
    document.body.classList.add('audience-' + currentAudience);
    document.body.setAttribute('data-audience', currentAudience);
  })();

  // Log initialization

})(window);
